import { useEffect, useState, useCallback } from "react";
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";
import { ChartBar, ChartLine, ChartPie } from "@phosphor-icons/react";
import api from "../lib/api";
import { fmt, fmtCompact, CHART_COLORS, GREEN } from "../lib/format";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";

const CHART_TYPES = [
  { v: "bar", icon: ChartBar, label: "Batang" },
  { v: "line", icon: ChartLine, label: "Garis" },
  { v: "pie", icon: ChartPie, label: "Lingkaran" },
];
const AGGS = [
  { v: "count", label: "Jumlah baris" },
  { v: "sum", label: "Total" },
  { v: "avg", label: "Rata-rata" },
];

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-border shadow-lg rounded-lg px-3 py-2 text-xs">
      <p className="text-muted-foreground mb-1">{label ?? payload[0]?.name}</p>
      <p className="font-display font-bold text-foreground tabular">{fmt(payload[0].value)}</p>
    </div>
  );
}

export default function ChartWidget({ dsId, columns, filters, title, initial, testid, month, monthColumn }) {
  const dims = columns.filter((c) => c.type === "string" || c.type === "date");
  const measures = columns.filter((c) => c.type === "number");

  const [type, setType] = useState(initial?.type || "bar");
  const [dimension, setDimension] = useState(initial?.dimension || dims[0]?.name || "");
  const [aggregation, setAggregation] = useState(initial?.aggregation || "count");
  const [measure, setMeasure] = useState(initial?.measure || measures[0]?.name || "");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    if (!dimension) return;
    setLoading(true);
    try {
      const { data: res } = await api.post(`/datasources/${dsId}/query`, {
        dimension, aggregation,
        measure: aggregation === "count" ? null : measure,
        filters, limit: type === "pie" ? 8 : 20,
        month: monthColumn ? month : null, month_column: monthColumn,
      });
      setData(res.data || []);
    } catch {
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [dsId, dimension, aggregation, measure, filters, type, month, monthColumn]);

  useEffect(() => { fetchData(); }, [fetchData]);

  return (
    <div className="rounded-2xl bg-card p-4 h-full flex flex-col shadow-sm ring-1 ring-black/[0.03]" data-testid={testid}>
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <h3 className="font-display font-bold text-base">{title}</h3>
        <div className="flex rounded-lg overflow-hidden ring-1 ring-border">
          {CHART_TYPES.map((t) => {
            const Icon = t.icon;
            return (
              <button key={t.v} onClick={() => setType(t.v)}
                className={`p-1.5 transition-colors ${type === t.v ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground hover:bg-secondary"}`}
                title={t.label} data-testid={`${testid}-type-${t.v}`}>
                <Icon size={15} weight="bold" />
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex gap-2 mt-3 flex-wrap">
        <Select value={dimension} onValueChange={setDimension}>
          <SelectTrigger className="h-8 text-xs w-auto min-w-[120px]" data-testid={`${testid}-dimension`}>
            <SelectValue placeholder="Dimensi" />
          </SelectTrigger>
          <SelectContent>
            {dims.map((d) => <SelectItem key={d.name} value={d.name}>{d.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={aggregation} onValueChange={setAggregation}>
          <SelectTrigger className="h-8 text-xs w-auto min-w-[110px]" data-testid={`${testid}-agg`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {AGGS.map((a) => <SelectItem key={a.v} value={a.v}>{a.label}</SelectItem>)}
          </SelectContent>
        </Select>
        {aggregation !== "count" && (
          <Select value={measure} onValueChange={setMeasure}>
            <SelectTrigger className="h-8 text-xs w-auto min-w-[110px]" data-testid={`${testid}-measure`}>
              <SelectValue placeholder="Ukuran" />
            </SelectTrigger>
            <SelectContent>
              {measures.map((m) => <SelectItem key={m.name} value={m.name}>{m.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>

      <div className="flex-1 min-h-[240px] mt-4">
        {loading ? (
          <div className="h-full flex items-center justify-center text-xs text-muted-foreground animate-pulse">Memuat grafik…</div>
        ) : data.length === 0 ? (
          <div className="h-full flex items-center justify-center text-xs text-muted-foreground">Tidak ada data</div>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            {type === "bar" ? (
              <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={GREEN.grid} vertical={false} />
                <XAxis dataKey="name" stroke={GREEN.axis} fontSize={10} tickLine={false} interval={0} angle={-25} textAnchor="end" height={60} />
                <YAxis stroke={GREEN.axis} fontSize={10} tickLine={false} axisLine={false} tickFormatter={fmtCompact} />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: "rgba(28,74,55,0.06)" }} />
                <Bar dataKey="value" fill={GREEN.dark} radius={[6, 6, 0, 0]} />
              </BarChart>
            ) : type === "line" ? (
              <LineChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={GREEN.grid} vertical={false} />
                <XAxis dataKey="name" stroke={GREEN.axis} fontSize={10} tickLine={false} angle={-25} textAnchor="end" height={60} />
                <YAxis stroke={GREEN.axis} fontSize={10} tickLine={false} axisLine={false} tickFormatter={fmtCompact} />
                <Tooltip content={<CustomTooltip />} />
                <Line type="monotone" dataKey="value" stroke={GREEN.dark} strokeWidth={2.5} dot={{ r: 2, fill: GREEN.dark }} activeDot={{ r: 5 }} />
              </LineChart>
            ) : (
              <PieChart>
                <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={95} innerRadius={52} paddingAngle={2}>
                  {data.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} stroke="#FFFFFF" strokeWidth={2} />)}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ fontSize: 11, color: "#6B7B72" }} />
              </PieChart>
            )}
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
